<template>
  <div class="container py-5 pt-3 pt-xl-5">
    <ProgressNavComponent
      v-if="signupStep >= 1 && !changingInformationDisabled"
      :steps="navSteps"
      :setStep="setStep"
    />

    <!-- STEP 1: Contact Review - Company and contact information -->
    <div v-if="shownStep.step_name === DefaultStepName.CONTACT_REVIEW">
      <CountryPickerComponent />

      <DefaultContactReviewConfigurationComponent
        v-if="companyIndex !== -1"
        :key="companyIndex"
        :accountData="accountData"
        :companyIndex="companyIndex"
        @handleUpdateCompany="signupStore.updateCompany"
        @updateAccountData="signupStore.updateAccount"
        @selectExistingCompany="handleSelectExistingCompany"
        @resetCompanySelection="handleResetCompanySelection"
        class="mb-4"
      />
    </div>

    <!-- STEP 2: Contract Review - Generic contract signing -->
    <DefaultContractReviewConfigurationComponent
      v-if="
        shownStep.step_name === DefaultStepName.CONTRACT_REVIEW &&
        companyIndex !== -1
      "
      :accountData="accountData"
      :setLoading="setLoading"
      @createContainerForStartSigningReady="storeCreateContainerForStartSigning"
      @disableChangingInformation="handleDisableChangingInformation"
      class="mb-4"
    />

    <!-- Navigation Buttons -->
    <div
      v-if="signupStep >= 1 && !changingInformationDisabled"
      class="d-flex justify-content-between w-100"
    >
      <button
        v-if="signupStep > 1"
        class="btn btn-secondary me-2"
        :disabled="isLoading"
        @click="setStep(signupStep - 1)"
      >
        {{ $t("signup.navsteps.back") }}
      </button>

      <!-- Spacer div when there's no back button -->
      <div v-else></div>

      <button
        class="btn btn-primary"
        :disabled="isLoading"
        @click="
          shownStep.step_name === DefaultStepName.CONTRACT_REVIEW
            ? createContainerForStartSigningFunction()
            : setStep(signupStep + 1)
        "
      >
        {{
          shownStep.step_name === DefaultStepName.CONTRACT_REVIEW
            ? $t("signup.navsteps.to_signature_page")
            : $t("signup.navsteps.next")
        }}
      </button>
    </div>

    <!-- Loading overlay -->
    <LoadingOverlayComponent v-if="isLoading" />

    <!-- Debug component for development -->
    <AccountDataDebugComponent :accountData="accountData" class="mb-4" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject } from "vue";
import AccountDataDebugComponent from "@/components/common/AccountDataDebugComponent.vue";
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import DefaultContactReviewConfigurationComponent from "@/signup/default/DefaultContactReviewConfigurationComponent.vue";
import DefaultContractReviewConfigurationComponent from "@/signup/default/DefaultContractReviewConfigurationComponent.vue";
import ProgressNavComponent from "@/signup/ProgressNavComponent.vue";
import CountryPickerComponent from "@/signup/CountryPickerComponent.vue";
import { useSignupStore } from "@/signup/signup";
import { useSignupStepSave } from "@/signup/composables/useSignupStepSave";
import { storeToRefs } from "pinia";
import { useQueryManager } from "@/composables/useQueryManager";
import { useStepFunctions, StepConfig } from "@/composables/useStepFunctions";
import useVuelidate from "@vuelidate/core";
import { useI18n } from "vue-i18n";
import { useContactReviewValidation } from "@/composables/useContactReviewValidation";

/**
 * Step enumeration for the default signup flow
 * Simple 2-step process for international countries
 */
enum DefaultStepName {
  CONTACT_REVIEW,
  CONTRACT_REVIEW,
}

const createContainerForStartSigningFunction = ref<() => void | Promise<void>>(
  () => undefined
);
const isLoading = ref(true);
const changingInformationDisabled = ref(false);

const globalErrorHandler = inject("globalErrorHandler") as (error: any) => void;
const signupStore = useSignupStore();
const {
  account: accountData,
  companyIndex,
  company: selectedCompany,
} = storeToRefs(signupStore);
const { updateQuery, getQueryValue } = useQueryManager();
const signupStep = getQueryValue("step", (value) =>
  value ? parseInt(value, 10) : 1
);
const countryQueryValue = getQueryValue(
  "country",
  (value) => value || "default"
);

const { t } = useI18n();
// Single source of truth for the contact-review validators, shared with
// DefaultContactReviewConfigurationComponent so this submit gate can't drift
// from the inline errors the user sees in the form.
const { accountRules, companyRules } = useContactReviewValidation(t);

const v$ = useVuelidate(
  { accountData: accountRules, company: companyRules },
  computed(() => ({
    accountData: accountData.value,
    company: selectedCompany.value || {},
  }))
);

const { postStepCallback } = useSignupStepSave(isLoading, v$);

/**
 * Step configuration for the default signup flow
 * Simple 2-step process: contact review and contract review
 * Uses dynamic country-based API endpoints
 */
const STEP_CONFIG = computed((): Record<DefaultStepName, StepConfig> => ({
  [DefaultStepName.CONTACT_REVIEW]: {
    step_name: DefaultStepName.CONTACT_REVIEW,
    title: t("signup.navsteps.default.contact_review"),
    titleKey: "signup.navsteps.default.contact_review",
    apiEndpoint: `/api/${countryQueryValue.value}/update-company`,
    postStepCallback: postStepCallback,
  },
  [DefaultStepName.CONTRACT_REVIEW]: {
    step_name: DefaultStepName.CONTRACT_REVIEW,
    title: t("signup.navsteps.default.contract_review"),
    titleKey: "signup.navsteps.default.contract_review",
    postStepCallback: postStepCallback,
  },
}));

/**
 * Flow steps configuration - simple linear 2-step process
 */
const flowSteps = computed(() => {
  return [
    STEP_CONFIG.value[DefaultStepName.CONTACT_REVIEW],
    STEP_CONFIG.value[DefaultStepName.CONTRACT_REVIEW],
  ];
});

const { shownStep, setStep, navSteps } = useStepFunctions(
  flowSteps,
  signupStep
);

const setLoading = (value: boolean) => {
  isLoading.value = value;
};

/**
 * Store the contract signing function for later use
 */
const storeCreateContainerForStartSigning = (
  createContainerForStartSigning: () => Promise<void>
) => {
  createContainerForStartSigningFunction.value = createContainerForStartSigning;
};

/**
 * Disable form editing after contract signing begins
 */
const handleDisableChangingInformation = () => {
  changingInformationDisabled.value = true;
};

/**
 * Handle existing company selection
 */
const handleSelectExistingCompany = async (companyId: number) => {
  isLoading.value = true;

  try {
    await updateQuery({
      company_id: companyId.toString(),
      step: "2",
    });
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

/**
 * Handle company selection reset
 */
const handleResetCompanySelection = () => {
  updateQuery({
    company_id: undefined,
    generator_id: undefined,
    step: "1",
  });
};

onMounted(async () => {
  // Ensure company exists for the flow
  signupStore.createCompanyIfMissing();
  isLoading.value = false;
});
</script>
